import styled, { css } from 'styled-components'
import {
  StyledFilterContainerProps,
  StyledFilterRowProps,
  StyledMoreFiltersButtonProps,
  StyledResultsSummaryProps,
  StyledActionButtonsProps,
} from './FilterContainer.types'

export const Container = styled.div<StyledFilterContainerProps>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme, $compact }) =>
    $compact ? theme.spacing.default : theme.spacing.medium};
  background: ${({ theme }) => theme.colors.background.primary};
  padding: ${({ theme, $compact }) =>
    $compact ? theme.spacing.default : theme.spacing.medium};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  border: 1px solid ${({ theme }) => theme.colors.border.light};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => theme.spacing.default};
    gap: ${({ theme }) => theme.spacing.default};
  }
`

export const FilterRow = styled.div<StyledFilterRowProps>`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme, $compact }) =>
    $compact ? theme.spacing.small : theme.spacing.default};
  align-items: flex-start;

  ${({ $layout }) => {
    switch ($layout) {
      case 'vertical':
        return css`
          flex-direction: column;
          align-items: stretch;

          & > * {
            width: 100%;
          }
        `
      case 'horizontal':
      default:
        return css`
          flex-direction: row;

          & > * {
            flex: 0 0 auto;
          }

          @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
            flex-direction: column;

            & > * {
              width: 100%;
            }
          }

          @media (max-width: ${({ theme }) => theme.breakpoints.largeMobile}) {
            gap: ${({ theme }) => theme.spacing.small};
          }
        `
    }
  }}
`

export const PrimaryFilters = styled(FilterRow)`
  position: relative;
`

export const AdditionalFilters = styled(FilterRow)<{ $expanded: boolean }>`
  overflow: hidden;
  transition: all ${({ theme }) => theme.transitions.medium};

  ${({ $expanded }) =>
    $expanded &&
    css`
      & > * {
        transform: translateY(0);
        transition: transform ${({ theme }) => theme.transitions.medium};
      }
    `}
`

export const FilterControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.default};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    align-items: stretch;
    gap: ${({ theme }) => theme.spacing.small};
  }
`

export const LeftControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.default};
  flex-wrap: wrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    justify-content: space-between;
  }
`

export const RightControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.default};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    justify-content: flex-end;
  }
`

export const MoreFiltersButton = styled.button<StyledMoreFiltersButtonProps>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.default};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  background: ${({ theme, $hasActiveFilters }) =>
    $hasActiveFilters
      ? theme.colors.tags.blue
      : theme.colors.background.primary};
  color: ${({ theme, $hasActiveFilters }) =>
    $hasActiveFilters ? theme.colors.primary : theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  position: relative;

  &:hover {
    background: ${({ theme, $hasActiveFilters }) =>
      $hasActiveFilters
        ? theme.colors.tags.blue
        : theme.colors.background.secondary};
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }

  ${({ $hasActiveFilters, theme }) =>
    $hasActiveFilters &&
    css`
      &::after {
        content: '';
        position: absolute;
        top: -2px;
        right: -2px;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: ${theme.colors.primary};
      }
    `}
`

export const MoreFiltersIcon = styled.span<{ $expanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  transition: transform ${({ theme }) => theme.transitions.fast};

  ${({ $expanded }) =>
    $expanded &&
    css`
      transform: rotate(180deg);
    `}
`

export const ClearAllButton = styled.button`
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.default};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  background: ${({ theme }) => theme.colors.background.primary};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
    color: ${({ theme }) => theme.colors.text.primary};
    border-color: ${({ theme }) => theme.colors.border.focus};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.border.focus};
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;

    &:hover {
      background: ${({ theme }) => theme.colors.background.primary};
      color: ${({ theme }) => theme.colors.text.secondary};
      border-color: ${({ theme }) => theme.colors.border.default};
    }
  }
`

export const ResultsSummary = styled.div<StyledResultsSummaryProps>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme, $compact }) =>
    $compact
      ? `${theme.spacing.small} ${theme.spacing.default}`
      : `${theme.spacing.default} ${theme.spacing.medium}`};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
`

export const ResultsCount = styled.span`
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`

export const ActionButtons = styled.div<StyledActionButtonsProps>`
  display: flex;
  gap: ${({ theme }) => theme.spacing.default};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 100%;

    & > button {
      flex: 1;
    }
  }
`

export const ApplyButton = styled.button`
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.medium};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.text.inverse};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  min-width: 80px;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primaryHover};
  }

  &:focus {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

export const FilterDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.border.light};
  margin: ${({ theme }) => theme.spacing.small} 0;
`

export const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  z-index: ${({ theme }) => theme.zIndex.elevated};
`

export const FilterItem = styled.div`
  position: relative;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 100%;
  }
`

export const ActiveFilterIndicator = styled.div`
  position: absolute;
  top: -2px;
  right: -2px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary};
  z-index: ${({ theme }) => theme.zIndex.elevated};
`
